import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { 
  TreePine, 
  Zap, 
  Droplets, 
  Leaf, 
  Fish, 
  Mountain,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  BarChart3,
  PieChart,
  Activity,
  Grid3X3,
  List,
  Heart,
  Award
} from 'lucide-react';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line } from 'recharts';
import { useCurrency } from '@/hooks/useCurrency';
import { SimpleProjectCard } from './SimpleProjectCard';
// import { ProjectCard } from './ProjectCard';
// import { DedicateModal } from './DedicateModal';
import { useToast } from '@/hooks/use-toast';

interface PortfolioAsset {
  id: string;
  assetType: string;
  projectName: string;
  location: string;
  imageUrl: string;
  quantity: number;
  averagePurchasePrice: number;
  currentValue: number;
  performance: number;
  vintage: number;
  carbonIntensity?: number;
  waterIntensity?: number;
  methaneReduction?: number;
  biodiversityScore?: number;
  certifications: string[];
  isPremium: boolean;
  acquisitionDate: string;
  status?: 'active' | 'for_sale' | 'retiring';
}

const mockPortfolioData: PortfolioAsset[] = [
  {
    id: '1',
    assetType: 'carbon_credits',
    projectName: 'Gujarat Wind Farm Project',
    location: 'Gujarat, India',
    imageUrl: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    quantity: 1500,
    averagePurchasePrice: 45,
    currentValue: 52,
    performance: 15.6,
    vintage: 2023,
    carbonIntensity: 0.85,
    biodiversityScore: 95,
    certifications: ['VCS', 'CCB', 'FSC'],
    isPremium: true,
    acquisitionDate: '2024-01-15',
    status: 'active'
  },
  {
    id: '2',
    assetType: 'renewable_energy_certificates',
    projectName: 'Solar Farm Maharashtra',
    location: 'Maharashtra, India',
    imageUrl: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    quantity: 800,
    averagePurchasePrice: 38,
    currentValue: 42,
    performance: 10.5,
    vintage: 2024,
    carbonIntensity: 0.02,
    certifications: ['I-REC', 'Gold Standard'],
    isPremium: false,
    acquisitionDate: '2024-02-20',
    status: 'for_sale'
  },
  {
    id: '3',
    assetType: 'low_methane_gas_credits',
    projectName: 'Biogas Plant Rajasthan',
    location: 'Rajasthan, India',
    imageUrl: 'https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    quantity: 600,
    averagePurchasePrice: 55,
    currentValue: 48,
    performance: -12.7,
    vintage: 2023,
    methaneReduction: 85,
    certifications: ['VCS', 'CDM'],
    isPremium: true,
    acquisitionDate: '2024-03-10',
    status: 'active'
  },
  {
    id: '4',
    assetType: 'water_credits',
    projectName: 'River Restoration Ganga',
    location: 'Uttar Pradesh, India',
    imageUrl: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    quantity: 400,
    averagePurchasePrice: 28,
    currentValue: 35,
    performance: 25.0,
    vintage: 2024,
    waterIntensity: 15,
    biodiversityScore: 88,
    certifications: ['Water Stewardship'],
    isPremium: false,
    acquisitionDate: '2024-04-05',
    status: 'active'
  },
  {
    id: '5',
    assetType: 'biodiversity_credits',
    projectName: 'Kerala Forest Conservation',
    location: 'Kerala, India',
    imageUrl: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    quantity: 300,
    averagePurchasePrice: 62,
    currentValue: 68,
    performance: 9.7,
    vintage: 2024,
    biodiversityScore: 98,
    certifications: ['VCS', 'CCB', 'FSC'],
    isPremium: true,
    acquisitionDate: '2024-05-12',
    status: 'active'
  }
];

const assetTypeConfig = {
  carbon_credits: { icon: TreePine, color: 'hsl(var(--success))', name: 'Carbon Credits' },
  renewable_energy_certificates: { icon: Zap, color: 'hsl(var(--warning))', name: 'RECs' },
  low_methane_gas_credits: { icon: Mountain, color: 'hsl(var(--info))', name: 'Low-Methane Gas' },
  sustainable_rice_credits: { icon: Leaf, color: 'hsl(var(--accent))', name: 'Sustainable Rice' },
  water_credits: { icon: Droplets, color: 'hsl(var(--primary))', name: 'Water Credits' },
  biodiversity_credits: { icon: Fish, color: 'hsl(var(--secondary))', name: 'Biodiversity' }
};

export const EnvironmentalPortfolioDashboard = () => {
  const [selectedAssetType, setSelectedAssetType] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [dedicateModalOpen, setDedicateModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<PortfolioAsset | null>(null);
  const { formatPrice } = useCurrency();
  const { toast } = useToast();

  const portfolioStats = useMemo(() => {
    const filteredAssets = selectedAssetType === 'all' 
      ? mockPortfolioData 
      : mockPortfolioData.filter(asset => asset.assetType === selectedAssetType);

    const totalValue = filteredAssets.reduce((sum, asset) => sum + (asset.quantity * asset.currentValue), 0);
    const totalCost = filteredAssets.reduce((sum, asset) => sum + (asset.quantity * asset.averagePurchasePrice), 0);
    const totalGainLoss = totalValue - totalCost;
    const totalPerformance = totalCost > 0 ? (totalGainLoss / totalCost) * 100 : 0;
    const totalQuantity = filteredAssets.reduce((sum, asset) => sum + asset.quantity, 0);

    return { totalValue, totalCost, totalGainLoss, totalPerformance, totalQuantity, assets: filteredAssets };
  }, [selectedAssetType]);

  const chartData = useMemo(() => {
    const assetTypeData = Object.entries(
      mockPortfolioData.reduce((acc, asset) => {
        const config = assetTypeConfig[asset.assetType as keyof typeof assetTypeConfig];
        const key = config?.name || asset.assetType;
        if (!acc[key]) {
          acc[key] = { name: key, value: 0, color: config?.color || 'hsl(var(--muted))' };
        }
        acc[key].value += asset.quantity * asset.currentValue;
        return acc;
      }, {} as any)
    ).map(([_, data]) => data);

    return assetTypeData;
  }, []);

  const performanceData = useMemo(() => {
    return mockPortfolioData.map(asset => ({
      name: asset.projectName.split(' ').slice(0, 2).join(' '),
      performance: asset.performance,
      value: asset.quantity * asset.currentValue
    }));
  }, []);

  const handleDedicate = (projectId: string, quantity: number, dedication: string) => {
    // In a real app, this would call the registry API
    console.log('Dedicating credits:', { projectId, quantity, dedication });
    
    toast({
      title: "Credits Dedicated to Climate Action",
      description: `Successfully retired ${quantity} credits from ${selectedProject?.projectName}`,
    });

    setDedicateModalOpen(false);
    setSelectedProject(null);
  };

  const handleSell = (projectId: string) => {
    // In a real app, this would navigate to the marketplace listing flow
    console.log('Listing project for sale:', projectId);
    
    toast({
      title: "Project Listed for Sale",
      description: "Your project has been added to the marketplace",
    });
  };

  const openDedicateModal = (projectId: string, quantity: number) => {
    const project = mockPortfolioData.find(p => p.id === projectId);
    if (project) {
      setSelectedProject(project);
      setDedicateModalOpen(true);
    }
  };

  return (
    <div className="space-y-6">
      {/* Portfolio Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-primary text-primary-foreground">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Total Portfolio Value</p>
                <p className="text-2xl font-bold">{formatPrice(portfolioStats.totalValue)}</p>
              </div>
              <DollarSign className="h-8 w-8 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Performance</p>
                <div className="flex items-center gap-2">
                  <p className={`text-2xl font-bold ${portfolioStats.totalPerformance >= 0 ? 'text-success' : 'text-destructive'}`}>
                    {portfolioStats.totalPerformance >= 0 ? '+' : ''}{portfolioStats.totalPerformance.toFixed(1)}%
                  </p>
                  {portfolioStats.totalPerformance >= 0 ? (
                    <TrendingUp className="h-5 w-5 text-success" />
                  ) : (
                    <TrendingDown className="h-5 w-5 text-destructive" />
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Assets</p>
                <p className="text-2xl font-bold">{portfolioStats.totalQuantity.toLocaleString()}</p>
              </div>
              <Activity className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Asset Types</p>
                <p className="text-2xl font-bold">{chartData.length}</p>
              </div>
              <PieChart className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Asset Type Filter */}
      <Card>
        <CardHeader>
          <CardTitle>Filter by Asset Type</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedAssetType === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedAssetType('all')}
              size="sm"
            >
              All Assets
            </Button>
            {Object.entries(assetTypeConfig).map(([type, config]) => {
              const Icon = config.icon;
              return (
                <Button
                  key={type}
                  variant={selectedAssetType === type ? 'default' : 'outline'}
                  onClick={() => setSelectedAssetType(type)}
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Icon className="h-4 w-4" />
                  {config.name}
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="overview">Portfolio Overview</TabsTrigger>
          <TabsTrigger value="assets">Asset Details</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Asset Allocation Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Asset Allocation</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                         {chartData.map((entry: any, index) => (
                           <Cell key={`cell-${index}`} fill={entry.color} />
                         ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatPrice(Number(value))} />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Performance Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Performance by Project</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={performanceData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip 
                        formatter={(value, name) => [
                          name === 'performance' ? `${value}%` : formatPrice(Number(value)),
                          name === 'performance' ? 'Performance' : 'Value'
                        ]}
                      />
                      <Bar dataKey="performance" fill="hsl(var(--primary))" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="assets" className="space-y-6">
          {/* View Mode Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">My Climate Portfolio</h3>
              <p className="text-sm text-muted-foreground">
                {portfolioStats.assets.length} projects • {portfolioStats.totalQuantity.toLocaleString()} total credits
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Project Cards Grid */}
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {portfolioStats.assets.map((asset) => (
                <SimpleProjectCard
                  key={asset.id}
                  project={{
                    id: asset.id,
                    projectName: asset.projectName,
                    location: asset.location,
                    quantity: asset.quantity,
                    currentValue: asset.currentValue
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {portfolioStats.assets.map((asset) => {
                const assetConfig = assetTypeConfig[asset.assetType as keyof typeof assetTypeConfig];
                const Icon = assetConfig?.icon || TreePine;
                
                return (
                  <Card key={asset.id} className="hover:shadow-elegant transition-all duration-300">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="p-3 rounded-full bg-muted" style={{ backgroundColor: `${assetConfig?.color}20` }}>
                            <Icon className="h-6 w-6" style={{ color: assetConfig?.color }} />
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex items-center gap-3">
                              <h3 className="font-semibold text-lg">{asset.projectName}</h3>
                              {asset.isPremium && (
                                <Badge variant="secondary" className="bg-gradient-primary text-primary-foreground">
                                  Premium
                                </Badge>
                              )}
                              {asset.status === 'for_sale' && (
                                <Badge variant="secondary" className="bg-warning/20 text-warning border-warning/30">
                                  For Sale
                                </Badge>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                Vintage {asset.vintage}
                              </span>
                              <span>{formatPrice(asset.currentValue)} per unit</span>
                            </div>
                            
                            <div className="flex flex-wrap gap-2">
                              {asset.certifications.map((cert) => (
                                <Badge key={cert} variant="outline" className="text-xs">
                                  {cert}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right space-y-2">
                          <div className="text-lg font-semibold">
                            {formatPrice(asset.quantity * asset.currentValue)}
                          </div>
                          <div className={`text-sm font-medium flex items-center gap-1 ${
                            asset.performance >= 0 ? 'text-success' : 'text-destructive'
                          }`}>
                            {asset.performance >= 0 ? (
                              <TrendingUp className="h-4 w-4" />
                            ) : (
                              <TrendingDown className="h-4 w-4" />
                            )}
                            {asset.performance >= 0 ? '+' : ''}{asset.performance.toFixed(1)}%
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {asset.quantity.toLocaleString()} units
                          </div>
                        </div>
                      </div>
                      
                      {/* ESG Metrics */}
                      {(asset.carbonIntensity || asset.waterIntensity || asset.methaneReduction || asset.biodiversityScore) && (
                        <>
                          <Separator className="my-4" />
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {asset.carbonIntensity && (
                              <div className="text-center">
                                <p className="text-xs text-muted-foreground">Carbon Intensity</p>
                                <p className="font-medium">{asset.carbonIntensity} kg CO₂e</p>
                              </div>
                            )}
                            {asset.waterIntensity && (
                              <div className="text-center">
                                <p className="text-xs text-muted-foreground">Water Intensity</p>
                                <p className="font-medium">{asset.waterIntensity}L/unit</p>
                              </div>
                            )}
                            {asset.methaneReduction && (
                              <div className="text-center">
                                <p className="text-xs text-muted-foreground">Methane Reduction</p>
                                <p className="font-medium">{asset.methaneReduction}%</p>
                              </div>
                            )}
                            {asset.biodiversityScore && (
                              <div className="text-center">
                                <p className="text-xs text-muted-foreground">Biodiversity Score</p>
                                <div className="space-y-1">
                                  <p className="font-medium">{asset.biodiversityScore}/100</p>
                                  <Progress value={asset.biodiversityScore} className="h-2" />
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}

                      {/* Action Buttons */}
                      {asset.status !== 'for_sale' && asset.status !== 'retiring' && (
                        <div className="flex gap-3 mt-4 pt-4 border-t border-border/50">
                          <Button
                            onClick={() => openDedicateModal(asset.id, asset.quantity)}
                            className="bg-gradient-to-r from-success to-success/80 hover:from-success/90 hover:to-success/70"
                            size="sm"
                          >
                            <Heart className="w-4 h-4 mr-2" />
                            Dedicate to Climate Action
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => handleSell(asset.id)}
                            size="sm"
                          >
                            List for Sale
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="performance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Portfolio Performance Over Time</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={[
                    { month: 'Jan', value: 95000 },
                    { month: 'Feb', value: 98000 },
                    { month: 'Mar', value: 102000 },
                    { month: 'Apr', value: 108000 },
                    { month: 'May', value: 115000 },
                    { month: 'Jun', value: portfolioStats.totalValue }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatPrice(Number(value))} />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={3}
                      dot={{ fill: 'hsl(var(--primary))' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Risk Analysis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Portfolio Diversification</span>
                    <span className="font-medium">85%</span>
                  </div>
                  <Progress value={85} className="h-2" />
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Vintage Risk</span>
                    <span className="font-medium">30%</span>
                  </div>
                  <Progress value={30} className="h-2" />
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Regulatory Risk</span>
                    <span className="font-medium">15%</span>
                  </div>
                  <Progress value={15} className="h-2" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>ESG Impact Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center p-4 bg-success/10 rounded-lg">
                  <TreePine className="h-8 w-8 mx-auto mb-2 text-success" />
                  <p className="text-2xl font-bold text-success">2,450 tCO₂e</p>
                  <p className="text-sm text-muted-foreground">Carbon Impact</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <Droplets className="h-6 w-6 mx-auto mb-1 text-primary" />
                    <p className="font-semibold">1.2M L</p>
                    <p className="text-xs text-muted-foreground">Water Saved</p>
                  </div>
                  <div>
                    <Fish className="h-6 w-6 mx-auto mb-1 text-secondary" />
                    <p className="font-semibold">95%</p>
                    <p className="text-xs text-muted-foreground">Avg Bio Score</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dedication Modal */}
      {/* <DedicateModal
        isOpen={dedicateModalOpen}
        onClose={() => {
          setDedicateModalOpen(false);
          setSelectedProject(null);
        }}
        project={selectedProject}
        onConfirm={handleDedicate}
      /> */}
    </div>
  );
};